import React from 'react';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter,
  Chip,
  Progress,
  Tabs,
  Tab
} from "@heroui/react";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

// Define interfaces for our data
interface KpiData {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  description: string;
}

interface Order {
  id: string;
  customer: string;
  priority: string;
  deadline: string;
  status: string;
  progress: number;
}



interface UpcomingOrder {
  id: string;
  customer: string;
  expectedDate: string;
  estimatedValue: number;
}

interface InventoryAlert {
  item: string;
  currentLevel: number;
  minLevel: number;
  status: 'critical' | 'warning';
}

const Dashboard: React.FC = () => {
  // Sample KPI data
  const kpiData: KpiData[] = [
    {
      title: "Pedidos",
      value: "142",
      change: "+12%",
      changeType: 'positive',
      description: "Total de pedidos activos"
    },
    {
      title: "Ordenes de Corte",
      value: "86",
      change: "+8%",
      changeType: 'positive',
      description: "Órdenes en proceso de corte"
    },
    {
      title: "Ordenes de Confección",
      value: "93",
      change: "+5%",
      changeType: 'positive',
      description: "Órdenes en proceso de confección"
    },
    {
      title: "Contratos Satélite",
      value: "24",
      change: "+3%",
      changeType: 'positive',
      description: "Contratos con proveedores satélite"
    },
    {
      title: "Producto Terminado",
      value: "312",
      change: "+15%",
      changeType: 'positive',
      description: "Unidades producidas este mes"
    },
    {
      title: "Despachos",
      value: "285",
      change: "+10%",
      changeType: 'positive',
      description: "Unidades despachadas este mes"
    }
  ];

  // Sample urgent orders data
  const urgentOrders: Order[] = [
    { id: "#ORD-001", customer: "Textil SAC", priority: "Alta", deadline: "2023-06-15", status: "En proceso", progress: 65 },
    { id: "#ORD-002", customer: "Fashion Group", priority: "Alta", deadline: "2023-06-18", status: "Corte", progress: 40 },
    { id: "#ORD-003", customer: "Moda Internacional", priority: "Alta", deadline: "2023-06-20", status: "Confección", progress: 25 },
    { id: "#ORD-004", customer: "Global Textiles", priority: "Alta", deadline: "2023-06-22", status: "Planificación", progress: 10 },
    { id: "#ORD-005", customer: "Trendy Ltd", priority: "Alta", deadline: "2023-06-25", status: "En proceso", progress: 50 }
  ];

  // Sample production status data for the pie chart (compatible with Recharts)
  const productionStatusData = [
    { name: "Planificación", percentage: 15, color: "#0088FE" },
    { name: "Corte", percentage: 25, color: "#00C49F" },
    { name: "Confección", percentage: 35, color: "#FFBB28" },
    { name: "Control Calidad", percentage: 20, color: "#FF8042" },
    { name: "Empaque", percentage: 5, color: "#8884d8" }
  ];

  // Sample upcoming orders from CRM
  const upcomingOrders: UpcomingOrder[] = [
    { id: "#CRM-001", customer: "Nueva Empresa Textil", expectedDate: "2023-07-10", estimatedValue: 45000 },
    { id: "#CRM-002", customer: "Modas del Norte", expectedDate: "2023-07-15", estimatedValue: 32000 },
    { id: "#CRM-003", customer: "Fashion Trends", expectedDate: "2023-07-18", estimatedValue: 28000 },
    { id: "#CRM-004", customer: "Textiles Premium", expectedDate: "2023-07-22", estimatedValue: 55000 },
    { id: "#CRM-005", customer: "Ropa Cómoda", expectedDate: "2023-07-25", estimatedValue: 15000 }
  ];

  // Sample inventory alerts
  const inventoryAlerts: InventoryAlert[] = [
    { item: "Hilo Algodón 30/1", currentLevel: 120, minLevel: 200, status: 'critical' },
    { item: "Tela Algodón Estampada", currentLevel: 350, minLevel: 500, status: 'warning' },
    { item: "Botones Plásticos", currentLevel: 80, minLevel: 150, status: 'critical' },
    { item: "Cremalleras", currentLevel: 180, minLevel: 250, status: 'warning' },
    { item: "Tela Poliéster Azul", currentLevel: 90, minLevel: 200, status: 'critical' }
  ];

  // Sample chart data for production status
  const orderStatusData = [
    { name: 'Planificación', Pedidos: 40 },
    { name: 'Corte', Pedidos: 30 },
    { name: 'Confección', Pedidos: 20 },
    { name: 'Control Calidad', Pedidos: 15 },
    { name: 'Empaque', Pedidos: 5 }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Dashboard de Producción Textil</h1>
        <p className="text-default-500">Resumen general de operaciones y producción</p>
      </div>

      {/* KPI Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {kpiData.map((kpi, index) => (
          <Card key={index} className="py-4 shadow-md">
            <CardHeader className="pb-0 pt-2 px-4 flex-col items-start">
              <p className="text-tiny uppercase font-bold">{kpi.title}</p>
              <small className="text-default-500">{kpi.description}</small>
            </CardHeader>
            <CardBody className="items-center justify-center py-3 px-4">
              <h4 className="font-bold text-4xl md:text-5xl">{kpi.value}</h4>
            </CardBody>
            <CardFooter className="gap-3">
              <div className="flex gap-1">
                <Chip 
                  color={kpi.changeType === 'positive' ? 'success' : 'danger'} 
                  size="sm"
                >
                  {kpi.change}
                </Chip>
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* Charts and Status Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Production Status Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Estado de Pedidos</h3>
          </CardHeader>
          <CardBody>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={productionStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="percentage"
                    label={({ name, percent }) => `${name}: ${(percent ?? 0 * 100).toFixed(0)}%`}
                  >
                    {productionStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>

        {/* Order Status Chart */}
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Distribución de Pedidos por Etapa</h3>
          </CardHeader>
          <CardBody>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={orderStatusData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Pedidos" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Tabs Section for Urgent Orders, Upcoming, and Alerts */}
      <Tabs aria-label="Dashboard Tabs" className="mb-6">
        <Tab key="urgent" title="Pedidos Urgentes">
          <Card>
            <CardBody>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left">
                      <th className="py-2 px-4">ID Pedido</th>
                      <th className="py-2 px-4">Cliente</th>
                      <th className="py-2 px-4">Prioridad</th>
                      <th className="py-2 px-4">Fecha Límite</th>
                      <th className="py-2 px-4">Estado</th>
                      <th className="py-2 px-4">Progreso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {urgentOrders.map((order, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="py-2 px-4">{order.id}</td>
                        <td className="py-2 px-4">{order.customer}</td>
                        <td className="py-2 px-4">
                          <Chip color="danger" variant="flat">
                            {order.priority}
                          </Chip>
                        </td>
                        <td className="py-2 px-4">{order.deadline}</td>
                        <td className="py-2 px-4">
                          <Chip color="primary" variant="flat">
                            {order.status}
                          </Chip>
                        </td>
                        <td className="py-2 px-4">
                          <div className="flex items-center">
                            <Progress 
                              size="sm" 
                              value={order.progress} 
                              className="max-w-md"
                              color={order.progress > 80 ? "success" : order.progress > 50 ? "warning" : "primary"}
                            />
                            <span className="ml-2 text-small">{order.progress}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Tab>

        <Tab key="upcoming" title="Clientes por Pedir (30 días)">
          <Card>
            <CardBody>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left">
                      <th className="py-2 px-4">ID CRM</th>
                      <th className="py-2 px-4">Cliente</th>
                      <th className="py-2 px-4">Fecha Estimada</th>
                      <th className="py-2 px-4">Valor Estimado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingOrders.map((order, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="py-2 px-4">{order.id}</td>
                        <td className="py-2 px-4">{order.customer}</td>
                        <td className="py-2 px-4">{order.expectedDate}</td>
                        <td className="py-2 px-4">${order.estimatedValue.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Tab>

        <Tab key="alerts" title="Alarmas de Inventario">
          <Card>
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {inventoryAlerts.map((alert, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">{alert.item}</h4>
                        <p className="text-sm text-default-500">
                          Nivel actual: {alert.currentLevel} | Mínimo: {alert.minLevel}
                        </p>
                      </div>
                      <Chip 
                        color={alert.status === 'critical' ? 'danger' : 'warning'} 
                        variant="flat"
                      >
                        {alert.status === 'critical' ? 'Crítico' : 'Advertencia'}
                      </Chip>
                    </div>
                    <Progress 
                      size="sm" 
                      value={(alert.currentLevel / alert.minLevel) * 100} 
                      className="mt-2"
                      color={alert.status === 'critical' ? "danger" : "warning"}
                    />
                  </Card>
                ))}
              </div>
            </CardBody>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
};

export default Dashboard;